import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // Auth states - token is now managed via HTTP-only cookies
  // We only store user info in state/localStorage for UI purposes
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status by calling the /me endpoint
  // The HTTP-only cookie will be sent automatically with the request
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}adminAuth/me`,
        { withCredentials: true }
      );
      if (response.data.success) {
        // /me endpoint returns { data: {...} }, login returns { user: {...} }
        const userData = response.data.user ?? response.data.data;
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem("admin_kihlotacademy_user", JSON.stringify(userData));
        }
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("admin_kihlotacademy_user");
    } finally {
      setChecked(true);
    }
  }, []);

  // Login function - called after successful login API response
  // Token is set via HTTP-only cookie by the backend, we only store user info
  const login = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Only store non-sensitive user info in localStorage for UI purposes
    localStorage.setItem("admin_kihlotacademy_user", JSON.stringify(userData));
  }, []);

  // Logout function - calls backend to clear the cookie
  const logout = useCallback(async () => {
    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}adminAuth/logout`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("admin_kihlotacademy_user");
      // Also remove old localStorage key if it exists
      localStorage.removeItem("admin_kihlotacademy");
    }
  }, []);

  useEffect(() => {
    // Try to restore user from localStorage for immediate UI, then verify with server
    const storedUser = localStorage.getItem("admin_kihlotacademy_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem("admin_kihlotacademy_user");
      }
    }
    // Also check for old localStorage key and migrate
    const oldStoredData = localStorage.getItem("admin_kihlotacademy");
    if (oldStoredData) {
      try {
        const adminData = JSON.parse(oldStoredData);
        if (adminData?.user) {
          setUser(adminData.user);
          setIsAuthenticated(true);
          // Migrate to new storage format
          localStorage.setItem("admin_kihlotacademy_user", JSON.stringify(adminData.user));
        }
        // Remove old format
        localStorage.removeItem("admin_kihlotacademy");
      } catch (e) {
        localStorage.removeItem("admin_kihlotacademy");
      }
    }
    // Verify auth with server
    checkAuth();
  }, [checkAuth]);

  // Provide backward compatibility - token is no longer needed for API calls
  // since cookies are automatically included, but keep for component compatibility
  return (
    <AuthContext.Provider value={{
      token: isAuthenticated ? 'cookie-managed' : null, // Backward compatibility marker
      user,
      checked,
      isAuthenticated,
      login,
      logout,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}
